import React, { FC, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { useTheme } from "@/context/Theme.context";
import { Button, ShowWhen, WarningBox } from "@/components/common";
import { addressDetailsChina, addressDetailsGlobal } from "../utils";
import { ButtonColor, Country, Theme } from "@/enums";
import { GET_ADDRESS_FOR_SHOPPING } from "@/constants/Query";
import { apiRequest } from "@/utils/apiRequest";
import { IAddressDetails } from "@/types/apiResponse.types";

import { AddressDetailsItem } from "./item";
import { useGetBasicUserDataRequest } from "@/api/useGetBasicUserDataRequest";
import { saveAddressToClipboard } from "@/utils/saveAddressToClipboard";
import useGetMainInfoDataRequest from "@/api/useGetMainInfoDataRequest";
import { useGetIsFullNecessaryDataOfClientRequest } from "@/api/useGetIsFullNecessaryDataOdClientRequest";

type Props = {
  selectedCountryId: number;
};

const AddressDetails: FC<Props> = ({ selectedCountryId }) => {
  const t = useTranslations();
  const theme = useTheme();
  const router = useRouter();

  const { data: roomDetails } = useGetBasicUserDataRequest();

  const { data, isLoading } = useQuery<{ data: { data: IAddressDetails[] } }>({
    queryKey: [GET_ADDRESS_FOR_SHOPPING, selectedCountryId],
    queryFn: () =>
      apiRequest(GET_ADDRESS_FOR_SHOPPING, "get", {
        fromCountryId: selectedCountryId,
      }),
    enabled: !!selectedCountryId,
  });

  const addressDetailsData = (data?.data?.data?.[0] || {}) as IAddressDetails;

  const { data: mainInfoData } = useGetMainInfoDataRequest();
  const mainInfo = mainInfoData?.data?.data;

  const { data: isFullNecessaryData, isLoading: isFullLoading } =
    useGetIsFullNecessaryDataOfClientRequest();

  const isFullData = isFullNecessaryData?.data?.data?.isFullData;

  const { addressDetails, isChina } = useMemo(() => {
    const isChinaCountry =
      selectedCountryId === Country.CHINA_AIR ||
      selectedCountryId === Country.CHINA_LAND;

    const isChina = isChinaCountry;

    return {
      addressDetails:
        (selectedCountryId === Country.CHINA_LAND && theme === Theme.UZ) ||
        ((selectedCountryId === Country.CHINA_AIR ||
          selectedCountryId === Country.CHINA_LAND) &&
          (theme === Theme.GEORGIA || theme === Theme.UK))
          ? addressDetailsChina
          : addressDetailsGlobal,
      isChina,
    };
  }, [selectedCountryId, theme]);

  return (
    <div className="h-full w-full rounded-3xl bg-white p-7 lg:max-w-[41%]">
      <h1 className="text-base font-bold uppercase text-black opacity-60">
        {t("addresses")}
      </h1>
      <ShowWhen condition={!isLoading && !isFullLoading && isFullData}>
        <div className="mt-3">
          <ShowWhen condition={!isChina}>
            <AddressDetailsItem
              address={{
                label: "full_name",
                value: `${mainInfo?.firstNameLatin || ""} ${mainInfo?.lastNameLatin || ""}`,
              }}
              editable
            />
          </ShowWhen>
          <ShowWhen condition={isChina}>
            <AddressDetailsItem
              address={{
                label: "name",
                value: `${roomDetails?.data?.data?.roomNumberToShow || ""} ${mainInfo?.firstNameLatin || ""}`,
              }}
              editable
            />
          </ShowWhen>
          <ShowWhen condition={isChina}>
            <AddressDetailsItem
              address={{
                label: "surname",
                value: mainInfo?.lastNameLatin || "",
              }}
            />
          </ShowWhen>
          {addressDetails?.map((address, index) => {
            const modifiedAddress = {
              ...address,
              value:
                selectedCountryId === Country.CHINA_AIR ||
                selectedCountryId === Country.CHINA_LAND
                  ? addressDetailsData?.extraFields?.[address.value]
                  : addressDetailsData[address.value],
            };
            if (!modifiedAddress?.value) {
              return null;
            }
            return (
              <AddressDetailsItem
                key={index}
                address={modifiedAddress}
                isLast={index === addressDetailsGlobal.length - 1}
                showSms={selectedCountryId === Country.TURKEY}
                roomNumber={roomDetails?.data?.data?.roomNumberToShow || ""}
                selectedCountryId={selectedCountryId}
              />
            );
          })}
          <ShowWhen condition={isChina}>
            <AddressDetailsItem
              address={{
                label: "receiver_name",
                value: ` ${roomDetails?.data?.data?.roomNumberToShow || ""} ${mainInfo?.firstNameLatin || ""} ${mainInfo?.lastNameLatin || ""}`,
              }}
            />
          </ShowWhen>
          <ShowWhen condition={isChina}>
            <AddressDetailsItem
              address={{
                label: "mobile",
                value: addressDetailsData?.extraFields?.mobile || "",
              }}
            />
          </ShowWhen>
          <ShowWhen condition={theme === Theme.UK}>
            <AddressDetailsItem
              address={{
                label: "working_hours",
                value: addressDetailsData?.extraFields?.openingHours || "",
              }}
            />
          </ShowWhen>
        </div>
        <Button
          color={ButtonColor.SECONDARY}
          onClick={() => {
            saveAddressToClipboard(addressDetailsData);
          }}
          className={`mt-5 w-auto ${theme !== Theme.UK ? "!text-[#0e7A89]" : ""}`}
        >
          {t("save_boxette_address_for_easy_shopping")}
        </Button>
      </ShowWhen>
      <ShowWhen condition={!isLoading && !isFullLoading && !isFullData}>
        <div className="flex flex-col gap-4">
          <WarningBox text="incomplete_profile_warning_text" />
          <Button
            onClick={() => {
              router.push("/profile/personal-information");
            }}
          >
            {t("go_to_profile")}
          </Button>
        </div>
      </ShowWhen>
    </div>
  );
};

export { AddressDetails };
